import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';
import { routinesApi } from '../services/routinesApi';
import {
  Play, ArrowRight, ArrowLeft, CheckCircle2, Circle, Loader2, MinusCircle,
  ShoppingCart, TrendingUp, RefreshCw, PlusCircle, ChevronRight, ExternalLink, Zap, AlertTriangle,
  SlidersHorizontal, Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const CYCLE_ICON = { p2p: ShoppingCart, otc: TrendingUp };
const TONE = {
  success: 'bg-green-100 text-green-700 border-green-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger: 'bg-red-100 text-red-700 border-red-200',
  info: 'bg-sky-100 text-sky-700 border-sky-200',
};

const StepDot = ({ status }) => {
  if (status === 'done') return <CheckCircle2 className="h-6 w-6 text-green-600" />;
  if (status === 'skipped') return <MinusCircle className="h-6 w-6 text-gray-300" />;
  if (status === 'current') return <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-600"><div className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" /></div>;
  return <Circle className="h-6 w-6 text-gray-300" />;
};

const Routines = () => {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [mode, setMode] = useState('resume');
  const [anchors, setAnchors] = useState([]);
  const [anchorLoading, setAnchorLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [runningAction, setRunningAction] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const loadAlerts = useCallback(() => {
    routinesApi.notifications(8).then((r) => setAlerts(r.data.items || [])).catch(() => setAlerts([]));
  }, []);

  useEffect(() => {
    routinesApi.cycles().then((r) => setCycles(r.data.cycles || [])).catch(() => setCycles([]));
    loadAlerts();
  }, [loadAlerts]);

  const openSettings = () => {
    setShowSettings(true);
    routinesApi.stageThresholds().then((r) => setCfg(r.data.cycles || [])).catch(() => setCfg([]));
  };

  const updateThreshold = (cycleKey, stageKey, val) => {
    setCfg((prev) => prev.map((c) => (c.cycle === cycleKey ? {
      ...c, stages: c.stages.map((s) => (s.key === stageKey ? { ...s, threshold_days: val } : s)),
    } : c)));
  };

  const saveSettings = async () => {
    setSavingCfg(true);
    try {
      await Promise.all((cfg || []).map((c) => routinesApi.setStageThresholds({
        cycle: c.cycle,
        thresholds: Object.fromEntries(c.stages.map((s) => [s.key, Number(s.threshold_days) || 0])),
      })));
      toast.success('Stall limits saved');
      setShowSettings(false);
      loadAlerts();
    } catch {
      toast.error('Could not save settings');
    } finally { setSavingCfg(false); }
  };

  const loadAnchors = useCallback((cy) => {
    setAnchorLoading(true);
    routinesApi.anchors(cy).then((r) => setAnchors(r.data.anchors || []))
      .catch(() => setAnchors([])).finally(() => setAnchorLoading(false));
  }, []);

  const loadProgress = useCallback((cy, id) => {
    setProgressLoading(true);
    routinesApi.progress(cy, id).then((r) => setProgress(r.data))
      .catch(() => toast.error('Could not load process progress')).finally(() => setProgressLoading(false));
  }, []);

  const openCycle = (cy) => {
    setCycle(cy); setMode('resume'); setSelected(null); setProgress(null);
    loadAnchors(cy.cycle);
  };

  const pickAnchor = (a) => { setSelected(a); loadProgress(cycle.cycle, a.id); };

  const openAlert = (alert) => {
    const cy = cycles.find((c) => c.cycle === alert.cycle);
    if (!cy) return;
    setCycle(cy); setMode('resume');
    const a = { id: alert.anchor_id, title: alert.anchor_id, subtitle: alert.title };
    setSelected(a);
    loadProgress(alert.cycle, alert.anchor_id);
    loadAnchors(alert.cycle);
  };

  const runApiAction = async (action) => {
    if (action.confirm && !window.confirm(action.confirm)) return;
    setRunningAction(action.endpoint);
    try {
      const path = action.endpoint.replace(/^\/api/, '');
      await api({ method: action.method || 'POST', url: path });
      toast.success(`${action.label} completed`);
      loadProgress(cycle.cycle, selected.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action.label} failed`);
    } finally { setRunningAction(null); }
  };

  const runAction = (action) => {
    if (action.type === 'navigate') navigate(action.path);
    else runApiAction(action);
  };

  const renderSettingsDialog = () => (
    <Dialog open={showSettings} onOpenChange={setShowSettings}>
      <DialogContent data-testid="routine-settings-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Stall Limits — nudge after N idle days</DialogTitle></DialogHeader>
        {!cfg ? (
          <div className="flex items-center gap-2 text-gray-400 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            {cfg.map((c) => (
              <div key={c.cycle}>
                <p className="text-sm font-semibold text-gray-800 mb-2">{c.title}</p>
                <div className="space-y-2">
                  {c.stages.map((s) => (
                    <div key={s.key} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600">{s.label}</span>
                      <div className="flex items-center gap-1.5">
                        <input type="number" min="0" value={s.threshold_days}
                          onChange={(e) => updateThreshold(c.cycle, s.key, e.target.value)}
                          data-testid={`threshold-${c.cycle}-${s.key}`}
                          className="w-20 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-orange-200" />
                        <span className="text-xs text-gray-400 w-8">days</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <button onClick={() => setShowSettings(false)}
            className="rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={saveSettings} disabled={savingCfg || !cfg}
            data-testid="routine-settings-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-60">
            {savingCfg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ---- cycle picker ----
  if (!cycle) {
    return (
      <Layout>
        <div className="p-6 max-w-5xl mx-auto" data-testid="routines-page">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-600 text-white"><Play className="h-5 w-5" /></div>
              <h1 className="text-2xl font-bold text-gray-900">Routines</h1>
            </div>
            <button onClick={openSettings} data-testid="routine-settings-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <SlidersHorizontal className="h-4 w-4" /> Stall Limits
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-8">Guided process runners that walk a document end-to-end — executing real workflow steps.</p>

          {alerts.length > 0 && (
            <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="routine-alerts">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Needs Attention — {alerts.length} stalled cycle(s)</span>
                </div>
                <button onClick={openSettings} data-testid="routine-alerts-settings"
                  className="text-xs font-medium text-amber-700 hover:underline inline-flex items-center gap-1">
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Adjust limits
                </button>
              </div>
              <div className="space-y-2">
                {alerts.map((al, i) => (
                  <button key={i} onClick={() => openAlert(al)} data-testid={`routine-alert-${i}`}
                    className="w-full flex items-center gap-3 rounded-xl border border-amber-200 bg-white px-4 py-2.5 hover:border-amber-400 hover:shadow-sm transition-all text-left">
                    <span className="text-[10px] uppercase font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">{al.cycle}</span>
                    <span className="font-medium text-gray-800">{al.anchor_id}</span>
                    <span className="text-sm text-gray-500 flex-1 truncate">stuck at <span className="font-medium text-gray-700">{al.current_stage_label}</span></span>
                    <span className="text-xs text-gray-400 shrink-0">limit {al.threshold_days}d</span>
                    <span className="text-xs font-semibold text-amber-700 shrink-0">{al.overdue_by}d over</span>
                    <ArrowRight className="h-4 w-4 text-amber-400 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cycles.map((cy) => {
              const Icon = CYCLE_ICON[cy.cycle] || Play;
              return (
                <button key={cy.cycle} onClick={() => openCycle(cy)} data-testid={`routine-cycle-${cy.cycle}`}
                  className="group text-left rounded-2xl border border-gray-200 bg-white p-6 hover:border-orange-300 hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors"><Icon className="h-6 w-6" /></div>
                    <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-orange-600 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{cy.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{cy.subtitle}</p>
                </button>
              );
            })}
          </div>
        </div>
        {renderSettingsDialog()}
      </Layout>
    );
  }

  // ---- inside a cycle ----
  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="routine-runner">
        <button onClick={() => setCycle(null)} data-testid="routine-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4">
          <ArrowLeft className="h-4 w-4" /> All routines
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{cycle.title}</h1>
        <p className="text-sm text-gray-500 mb-6">{cycle.subtitle}</p>

        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 mb-6">
          <button onClick={() => setMode('resume')} data-testid="routine-mode-resume"
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'resume' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}>
            <RefreshCw className="h-4 w-4" /> Resume Existing
          </button>
          <button onClick={() => setMode('new')} data-testid="routine-mode-new"
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${mode === 'new' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}>
            <PlusCircle className="h-4 w-4" /> Start New
          </button>
        </div>

        {mode === 'new' && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="routine-startnew">
            <p className="text-sm text-gray-500 mb-4">Follow the steps in order. Each step opens the real create screen and posts a genuine document.</p>
            <ol className="space-y-3">
              {cycle.start_new.map((s, i) => (
                <li key={i}>
                  <button onClick={() => navigate(s.path)} data-testid={`routine-startnew-step-${i}`}
                    className="w-full flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 hover:border-orange-300 hover:bg-orange-50/40 transition-colors text-left">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-100 text-orange-700 text-sm font-semibold">{i + 1}</span>
                    <span className="flex-1 text-sm font-medium text-gray-800">{s.label}</span>
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}

        {mode === 'resume' && !selected && (
          <div data-testid="routine-anchor-list">
            <p className="text-sm text-gray-500 mb-3">Pick an in-flight {cycle.anchor_label.toLowerCase()} to continue its cycle.</p>
            {anchorLoading ? (
              <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : anchors.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400">No in-flight documents. Use "Start New" to begin.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {anchors.map((a) => (
                  <button key={a.id} onClick={() => pickAnchor(a)} data-testid={`routine-anchor-${a.id}`}
                    className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-orange-300 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">{a.title}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${TONE[a.status_tone] || TONE.info}`}>{a.status}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">{a.subtitle}</p>
                    {a.amount && <p className="text-sm font-medium text-gray-700 mt-2">{a.amount}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === 'resume' && selected && (
          <div data-testid="routine-progress">
            <button onClick={() => { setSelected(null); setProgress(null); }} data-testid="routine-anchor-back"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4">
              <ArrowLeft className="h-4 w-4" /> Choose another
            </button>
            {progressLoading || !progress ? (
              <div className="flex items-center gap-2 text-gray-400 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Tracing document flow…</div>
            ) : (
              <>
                <div className="rounded-2xl border border-gray-200 bg-white p-5 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400">{cycle.anchor_label}</p>
                      <p className="text-lg font-bold text-gray-900">{progress.anchor.id}</p>
                      <p className="text-sm text-gray-500">{progress.anchor.label}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-orange-600">{Math.round(progress.completion * 100)}%</p>
                      <p className="text-xs text-gray-400">complete</p>
                    </div>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-orange-600 transition-all" style={{ width: `${progress.completion * 100}%` }} />
                  </div>
                  {progress.is_complete && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-700"><CheckCircle2 className="h-4 w-4" /> Cycle fully completed.</div>
                  )}
                </div>

                <div className="space-y-0">
                  {progress.stages.map((st, i) => {
                    const isLast = i === progress.stages.length - 1;
                    return (
                      <div key={st.key} className="flex gap-4" data-testid={`routine-stage-${st.key}`}>
                        <div className="flex flex-col items-center">
                          <StepDot status={st.status} />
                          {!isLast && <div className={`w-0.5 flex-1 min-h-[2rem] ${st.status === 'done' || st.status === 'skipped' ? 'bg-green-200' : 'bg-gray-200'}`} />}
                        </div>
                        <div className={`flex-1 pb-6 ${st.status === 'current' ? '' : ''}`}>
                          <div className={`rounded-xl border p-4 ${st.status === 'current' ? 'border-orange-300 bg-orange-50/40' : 'border-gray-200 bg-white'}`}>
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-gray-900">{st.label}
                                {st.status === 'skipped' && <span className="ml-2 text-xs font-normal text-gray-400">(not used)</span>}
                              </h4>
                              {st.status === 'current' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-600 text-white font-medium">CURRENT</span>}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">{st.description}</p>
                            {st.documents.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {st.documents.map((d) => (
                                  <button key={d.id} onClick={() => d.view_path && navigate(d.view_path)}
                                    data-testid={`routine-doc-${d.id}`}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs hover:border-orange-300">
                                    <span className="font-medium text-gray-700">{d.label}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] border ${TONE[d.status_tone] || TONE.info}`}>{d.status}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {st.actions.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {st.actions.map((a, ai) => {
                                  const isApi = a.type === 'api';
                                  const busy = runningAction === a.endpoint;
                                  return (
                                    <button key={ai} onClick={() => runAction(a)} disabled={busy}
                                      data-testid={`routine-action-${st.key}-${ai}`}
                                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${isApi ? 'bg-orange-600 text-white hover:bg-orange-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isApi ? <Zap className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                      {a.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Routines;
